import React from "react";
import { Link, useLocation } from "react-router-dom";

import { FeatureFlagNames, useFeatureFlags } from "@/providers/featureFlags";
import { OrganizationSwitcher } from "@/components/common/OrganizationSwitcher";
import { Logo } from "@/components/ui/Logo";
import { ChevronLeft24 } from "@/components/ui/SvgIcon";

import { useHideDemoKitExtraneousPages } from "@/features/enroll/onboarding/hooks/useHideDemoKitExtraneousPages";

import cn from "@/utils/cn";

import { SidebarNavParentItem } from "./components/SidebarNavParentItem";
import { getBottomNavItems, getMainNavItems } from "./config/mainNavConfig";
import { useExpandedNavSections } from "./hooks/useExpandedNavSections";
import { useSidebarNav } from "./useSidebarNav";

export const SidebarNav = () => {
  const pathname = useLocation().pathname;
  const { isCollapsed, toggleSidebar } = useSidebarNav();
  const { isExpanded, toggleExpanded } = useExpandedNavSections();
  const { isEnabled } = useFeatureFlags();

  const showSubpagesInSideMenu = isEnabled(
    FeatureFlagNames.ShowSubpagesInSideMenu,
  );
  const hideDemoKitPages = useHideDemoKitExtraneousPages();
  const mainNavItems = getMainNavItems(
    showSubpagesInSideMenu,
    hideDemoKitPages,
  );
  const bottomNavItems = getBottomNavItems(
    showSubpagesInSideMenu,
    hideDemoKitPages,
  );

  return (
    <div
      className={cn(
        "relative flex h-full w-full flex-col gap-y-[18px] overflow-hidden bg-chorus-black-500 transition-all duration-300 ease-in-out",
      )}
    >
      <div
        className={cn(
          "mt-6 flex h-16 shrink-0 items-center transition-all duration-300",
          isCollapsed ? "justify-center px-0" : "pl-8 pr-6",
        )}
      >
        {isCollapsed ? (
          <Link to="/" className="transition-opacity duration-300">
            <svg
              width="26"
              height="48"
              viewBox="0 0 26 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M25.2406 0H21.7383V19.5537H25.2406V0Z" fill="#EF652B" />
              <path
                d="M25.2406 43.3662H21.7383V46.8687H25.2406V43.3662Z"
                fill="#EF652B"
              />
              <path
                d="M16.0149 40.8984L12.9258 46.2489L15.9589 48.0002L19.048 42.6495L16.0149 40.8984Z"
                fill="#EF652B"
              />
              <path
                d="M12.3008 35.8984L4.63086 40.3267L6.38197 43.3599L14.0519 38.9316L12.3008 35.8984Z"
                fill="#EF652B"
              />
              <path
                d="M11.5807 29.709H0.0527344V33.2113H11.5807V29.709Z"
                fill="#EF652B"
              />
              <path
                d="M1.75117 16.8877L0 19.9208L12.303 27.0239L14.0541 23.9908L1.75117 16.8877Z"
                fill="#EF652B"
              />
              <path
                d="M10.6054 5.65039L7.57227 7.40157L16.0146 22.0239L19.0476 20.2726L10.6054 5.65039Z"
                fill="#EF652B"
              />
            </svg>
          </Link>
        ) : (
          <>
            <Link to="/" className="transition-opacity duration-300">
              <Logo />
            </Link>
            <button
              onClick={toggleSidebar}
              className={cn(
                "ml-auto mt-4 hidden size-8 items-center justify-center rounded-md transition-all duration-300 ease-in-out hover:bg-chorus-black-400 xl:flex",
                isCollapsed
                  ? "pointer-events-none translate-x-4 scale-0 opacity-0"
                  : "translate-x-0 scale-100 opacity-100",
              )}
              style={{
                transition:
                  "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              aria-label="Collapse sidebar"
              data-testid="sidebar-collapse-button"
            >
              <ChevronLeft24
                className={cn(
                  "fill-chorus-gray-300 transition-transform duration-300",
                  isCollapsed ? "rotate-180" : "rotate-0",
                )}
              />
            </button>
          </>
        )}
      </div>
      <nav
        className={cn(
          "flex flex-1 flex-col pb-4",
          isCollapsed
            ? "no-scrollbar overflow-hidden"
            : "no-scrollbar overflow-y-auto",
        )}
      >
        <div role="list" className="flex flex-1 flex-col">
          <div
            data-testid="sidebar-main-nav"
            className={cn(
              "flex flex-col gap-0.5 transition-all duration-300",
              isCollapsed ? "px-2" : "px-6",
            )}
          >
            {mainNavItems.map((item) => (
              <SidebarNavParentItem
                key={item.id}
                item={item}
                pathname={pathname}
                isCollapsed={isCollapsed}
                isExpanded={isExpanded(item.id)}
                onToggleExpanded={() => toggleExpanded(item.id)}
              />
            ))}
          </div>
          <div
            data-testid="sidebar-bottom-nav"
            className={cn(
              "mt-auto flex flex-col gap-0.5 transition-all duration-300",
              isCollapsed ? "px-2" : "px-6",
            )}
          >
            {bottomNavItems.map((item) => (
              <SidebarNavParentItem
                key={item.id}
                item={item}
                pathname={pathname}
                isCollapsed={isCollapsed}
                isExpanded={isExpanded(item.id)}
                onToggleExpanded={() => toggleExpanded(item.id)}
              />
            ))}
          </div>
          <div className="my-4 border-t border-chorus-black-100" />
          <div
            className={cn(
              "transition-all duration-300",
              isCollapsed ? "px-2" : "px-3",
            )}
          >
            <OrganizationSwitcher isCollapsed={isCollapsed} />
          </div>
        </div>
      </nav>
    </div>
  );
};
